import { IResponseData, IWorkWearStoreAvailabilityData, SizesScalePriceResponse } from './types';

export class HttpError extends Error {
    status: number;
    response: Response;

    constructor(response: Response) {
        super(`HTTP error ${response.status}`);
        this.status = response.status;
        this.response = response;
    }
}

export class Fetch {
    private apiLink: string;
    private readonly isMobile: boolean | undefined;

    private detailComponentEndpoint = 'productdetailspage/componentsdata';
    constructor(apiLink: string) {
        this.apiLink = apiLink;
        this.handleError.bind(this);
    }

    private async fetchJson<T>(url: string, timeoutMs = 5000, init?: RequestInit): Promise<T> {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), timeoutMs);
        try {
            const response = await fetch(url, { signal: controller.signal, ...init });
            if (!response.ok) throw new HttpError(response);
            return response.json() as Promise<T>;
        } finally {
            clearTimeout(timer);
        }
    }

    private handleError(error: Error | HttpError): null {
        const errorMsg = error instanceof HttpError ? error.message : `${error.name}: ${error.message}`;
        const errorStack = error instanceof HttpError ? `status: ${error.status}` : error.stack;
        // eslint-disable-next-line no-console
        console.warn(`ajax fetch failed with "${errorMsg}". Full error:\n${errorStack}`);
        return null;
    }

    public fetch(
        salesArticleNo: string,
        colorCode: string,
        masterArticleNo: number,
        sizeCode: string,
        navigationKey?: string,
        wwsStatus?: string
    ): Promise<IResponseData | null> {
        const apiUrl = this.apiLink + this.detailComponentEndpoint;
        const requestUrl = new URL(apiUrl);

        // Append all query parameters from the current browser URL
        if (typeof window !== 'undefined') {
            const currentUrl = new URL(window.location.href);

            currentUrl.searchParams.forEach((value, key) => {
                requestUrl.searchParams.append(key, value);
            });
        }

        requestUrl.searchParams.set('salesArticleNo', salesArticleNo);
        requestUrl.searchParams.set('colorCode', colorCode);
        requestUrl.searchParams.set('masterArticleNo', masterArticleNo.toString());

        if (navigationKey) requestUrl.searchParams.set('navigationKey', navigationKey);
        if (sizeCode) requestUrl.searchParams.set('sizeCode', sizeCode);
        if (wwsStatus) requestUrl.searchParams.set('availabilityStatus', wwsStatus);

        return this.fetchJson<IResponseData>(requestUrl.toString(), 3500)
            .catch(error => this.handleError(error));
    }

    public getPriceListForSizesScaled(
        apiLink: string,
        sizeQuantities: Record<string, number>,
    ): Promise<SizesScalePriceResponse | null> {
        const apiUrl = apiLink + 'productdetailspage/sizespricescaled';

        return this.fetchJson<SizesScalePriceResponse>(apiUrl, 3500, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(sizeQuantities),
        }).catch(error => this.handleError(error));
    }

    public setPriceModeCookie(newPriceMode: 'Net' | 'Gross'): void {
        const urlToHost = this.apiLink.replace('api/', '');
        const apiUrl = urlToHost + 'ajax/update/Content/Empty2';
        const requestUrl = new URL(apiUrl);
        requestUrl.searchParams.set('PriceMode', newPriceMode);

        fetch(requestUrl.toString())
            .then(response => {
                if (response.ok && typeof window !== 'undefined') {
                    window.shell.publishTo('PriceModeChanged', newPriceMode);
                }
            })
            .catch((error: Error) => {
                // eslint-disable-next-line no-console
                console.warn(`fetch() failed with "${error.name}: ${error.message}". Full error:\n${error.stack}`);
            });
    }

    public updateAvailableInWws(
        apiLink: string,
        portal: string,
        masterArticleNo: number
    ): Promise<IWorkWearStoreAvailabilityData | null> {
        const companyNo = portal.includes('AT') ? 2 : 1;
        const apiUrl = new URL(apiLink);
        apiUrl.searchParams.set('masterArticleNo', masterArticleNo.toString());
        apiUrl.searchParams.set('companyNo', companyNo.toString());

        return this.fetchJson<IWorkWearStoreAvailabilityData>(apiUrl.toString())
    }
}
